import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IAccountsReceivable, ISelectedCenterOrgan } from '@nkr/models/index';
import { ReportWrapper } from './styles';
import { AgGridReact } from 'ag-grid-react';
import { CellClassParams, ColDef, ColGroupDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SelectionChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Utility } from '@nkr/services/utility';
import { IInvoiceSummary } from '@nkr/store/billing/actions-admin-invoice';
import { Label } from 'react-bootstrap';

interface IProps {
    data: IAccountsReceivable[],
    onGridReady: (e: GridReadyEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void,
    onRowDataChanged: (e: RowDataChangedEvent) => void,
    setSelectedCenters: (selections: ISelectedCenterOrgan[]) => void
}

const Grid = (props: IProps) => {

    const getTotals = (): IAccountsReceivable[] => {
        let totals = {
            centerId: 0,
            centerAlias: '',
            centerPaysViaAch: false,
            organType: 0,
            centerExecutiveSponsor: '',
            lastLateNoticeDate: '',
            pending0To30: 0,
            pending31To45: 0,
            pending46To60: 0,
            pending61To90: 0,
            pending91To120: 0,
            pending121To150: 0,
            pendingOver90: 0,
            pendingOver150: 0,
            totalDelinquent: 0,
            ccbApTotal: 0,
            totalOutstanding: 0
        };

        props.data.forEach(item => {
            totals.pending0To30 += item.pending0To30;
            totals.pending31To45 += item.pending31To45;
            totals.pending46To60 += item.pending46To60;
            totals.pending61To90 += item.pending61To90;
            totals.pending91To120 += item.pending91To120;
            totals.pending121To150 += item.pending121To150;
            totals.pendingOver90 += item.pendingOver90;
            totals.pendingOver150 += item.pendingOver150;
            totals.totalDelinquent += item.totalDelinquent;
            totals.ccbApTotal += item.ccbApTotal;
            totals.totalOutstanding += item.totalOutstanding;
        });

        return [totals];
    };

    const handleSelectionChanged = (e: SelectionChangedEvent) => {
        const selections = e.api.getSelectedNodes()
            .map(n => n.data)
            .filter(x => x && x.centerId > 0)
            .map(x => ({ centerId: x.centerId, organType: x.organType ?? null }));
        props.setSelectedCenters(selections);
    };

    const renderValue = (value: number) => {
        if (value == 0) {
            return <>-</>;
        }

        return <>{value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    };

    const columnDefs = useMemo((): Array<ColDef | ColGroupDef> => [
        {
            headerName: '-',
            headerClass: 'empty-header',
            children: [
                {
                    colId: 'checkbox',
                    headerName: 'checkbox',
                    headerClass: 'list-header',
                    cellClass: 'list-cell-center',
                    headerComponentFramework: (params: IHeaderParams) => (
                        <input
                            type='checkbox'
                            onChange={(e) => {
                                e.stopPropagation();
                                const api: any = (params as any).api || (params as any).columnApi?.gridApi;
                                if (!api) { return; }
                                if (e.currentTarget.checked) {
                                    api.deselectAll();
                                    api.forEachNode((node: any) => {
                                        const d = node.data;
                                        if (d && d.centerId > 0 && (d.totalDelinquent - d.ccbApTotal) > 0) {
                                            api.selectNode(node, true);
                                        }
                                    });
                                } else {
                                    api.deselectAll();
                                }
                            }}
                        />
                    ),
                    checkboxSelection: (params) => !!(params.data && params.data.centerId > 0),
                    width: 30,
                    suppressSizeToFit: true,
                },
                {
                    colId: 'rank',
                    headerName: 'Rank',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            R#
                        </SortableHeaderComponent>,
                    headerClass: 'list-header',
                    cellClass: 'list-cell-center',
                    width: 50,
                    suppressSizeToFit: true,
                    sortable: false,
                    cellRendererFramework: (params: ICellRendererParams) => {
                        if (params.node.rowPinned) return <></>;   // ← skip pinned row
                        return <>{params.node.rowIndex + 1}</>;
                    },
                    valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
                },

                {
                    colId: 'organType',
                    field: 'organType',
                    headerName: 'Organ Type',
                    headerComponentFramework: (params: IHeaderParams) =>
                        <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
                    width: 70,
                    cellClass: 'list-cell-center',
                    cellRendererFramework: (params: ICellRendererParams) => {
                        if (params.node.rowPinned) return <></>;   // ← skip pinned row
                        let data = params.data as IAccountsReceivable;
                        let organType = data.organType == null ? '--' : data.organType == 1 ? 'L' : 'K';
                        return <Label bsStyle={data.organType == null ? 'default' : data.organType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
                    }
                },

                {
                    colId: 'centerAlias',
                    field: 'centerAlias',
                    headerName: 'Center',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
                    width: 155,
                    cellClass: (params: CellClassParams) => (params.data.totalDelinquent - params.data.ccbApTotal) > 0 ? 'list-cell-center bg-risk' : 'list-cell-center',
                    cellRendererFramework: (props: ICellRendererParams) => props.value == '' ? <>Totals</> : <>{props.value}</>
                }
            ]
        },

        {
            headerName: 'Current',
            headerClass: 'alt-header',
            children: [
                {
                    colId: 'pending0To30',
                    field: 'pending0To30',
                    headerName: '0-30 Days',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>0-30 Days</SortableHeaderComponent>,
                    width: 100,
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => renderValue(props.value)
                },
                {
                    colId: 'pending31To45',
                    field: 'pending31To45',
                    headerName: '31-45 Days',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>31-45 Days</SortableHeaderComponent>,
                    width: 100,
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => renderValue(props.value)
                }
            ]
        },

        {
            headerName: 'Delinquent',
            headerClass: 'alt-header',
            children: [
                {
                    colId: 'pending46To60',
                    field: 'pending46To60',
                    headerName: '46-60 Days',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>46-60 Days</SortableHeaderComponent>,
                    width: 100,
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => renderValue(props.value)
                },
                {
                    colId: 'pending61To90',
                    field: 'pending61To90',
                    headerName: '61-90 Days',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>61-90 Days</SortableHeaderComponent>,
                    width: 100,
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => renderValue(props.value)
                },
                {
                    colId: 'pendingOver90',
                    field: 'pendingOver90',
                    headerName: '91+ Days',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>91+ Days</SortableHeaderComponent>,
                    width: 100,
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => renderValue(props.value)
                }
            ]
        },

        {
            headerName: '-',
            headerClass: 'empty-header',
            children: [

                {
                    colId: 'totalDelinquent',
                    field: 'totalDelinquent',
                    headerName: 'Total Delinquent',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total<br />Delinquent</SortableHeaderComponent>,
                    width: 125,
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => renderValue(props.value)
                },

                {
                    colId: 'ccbApTotal',
                    field: 'ccbApTotal',
                    headerName: 'CCB AP',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NKR CCB AP<br />151+ Days</SortableHeaderComponent>,
                    width: 125,
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => renderValue(props.value)
                },

                {
                    colId: 'netRisk',
                    field: 'netRisk',
                    headerName: 'Net Risk',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Net Risk*</SortableHeaderComponent>,
                    width: 125,
                    sortingOrder: ['desc', 'asc'],
                    cellClass: (params: CellClassParams) => (params.data.totalDelinquent - params.data.ccbApTotal) > 0 ? 'list-cell bg-risk' : 'list-cell',
                    cellRendererFramework: (props: ICellRendererParams) => {
                        let data = props.data as IAccountsReceivable;

                        return renderValue(data.totalDelinquent - data.ccbApTotal);
                    },
                    valueGetter: (params: ValueGetterParams) => {
                        return params.data.totalDelinquent - params.data.ccbApTotal;
                    }
                },

                {
                    colId: 'totalOutstanding',
                    field: 'totalOutstanding',
                    headerName: 'Total Outstanding',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total<br />Outstanding</SortableHeaderComponent>,
                    width: 135,
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => renderValue(props.value)
                },

                {
                    colId: 'centerPaysViaAch',
                    field: 'centerPaysViaAch',
                    headerName: 'ACH',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>ACH</SortableHeaderComponent>,
                    width: 50,
                    cellClass: 'list-cell-center',
                    sortingOrder: ['desc', 'asc'],
                    cellRendererFramework: (props: ICellRendererParams) => props.value == false ? '-' : <span className='label label-success'>Y</span>
                },

                {
                    colId: 'centerExecutiveSponsor',
                    field: 'centerExecutiveSponsor',
                    headerName: 'Executive Sponsor',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Executive<br />Sponsor</SortableHeaderComponent>,
                    width: 135,
                    cellClass: 'list-cell-center',
                },

                {
                    colId: 'lastLateNoticeDate',
                    field: 'lastLateNoticeDate',
                    headerName: 'Email Date',
                    headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Email<br />Date</SortableHeaderComponent>,
                    width: 135,
                    cellClass: 'list-cell-center',
                    comparator: Utility.dateComparator
                }
            ]
        }
    ], []);

    const rowData = useMemo(() => props.data, [props.data]);
    const totals = useMemo(() => getTotals(), [props.data]);

    // Keep a ref to the grid API so we can fit columns after selection/data changes
    const apiRef = useRef<import('ag-grid-community').GridApi | null>(null);

    // After selection changes or data arrives, fit columns to available width
    useEffect(() => {
        if (apiRef.current) {
            setTimeout(() => {
                try { apiRef.current!.sizeColumnsToFit(); } catch { }
            }, 0);
        }
    }, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    cellClassRules: {
                        'totalsBackground': (params) => {
                            return params.data.centerId == 0;
                        }
                    },
                    sortable: true,
                    resizable: false
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={50}
                groupHeaderHeight={30}
                rowHeight={30}
                rowSelection={'multiple'}
                suppressRowClickSelection={true}

                pinnedTopRowData={totals}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                excelStyles={[
                    {
                        id: 'header',
                        alignment: {
                            horizontal: 'Center'
                        },
                        font: {
                            bold: true
                        }
                    },
                    {
                        id: 'cell',
                        alignment: {
                            horizontal: 'Center'
                        }
                    },
                    {
                        id: 'totalsBackground',
                        interior: {
                            color: '#ADD8E6',
                            pattern: 'Solid',
                            patternColor: 'black'
                        }
                    }
                ]}
                onGridReady={(e) => {
                    apiRef.current = e.api;
                    if (props.onGridReady) props.onGridReady(e);
                }}
                onFirstDataRendered={props.onFirstDataRendered}
                onSortChanged={(e: SortChangedEvent) => {
                    e.api.refreshCells();
                }}
                onRowDataChanged={props.onRowDataChanged}
                onSelectionChanged={handleSelectionChanged}
                getRowStyle={(props: any) => {
                    let data = props.data as IAccountsReceivable;

                    if (data.centerId == 0) {
                        return { backgroundColor: 'lightblue', fontWeight: 700 } as any;
                    }
                }}
                getRowNodeId={(data) => (data && data.centerId ? `${data.centerId}-${data.organType}` : '')}
            />
        </ReportWrapper>
    );
}

export default Grid;